<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between align-center" cols="12">
                <div>
                    <h1 class="text-left text-h5 font-weight-bold">Speeding Rules</h1>
                    <p class="text-body-2 text-medium-emphasis text-left mb-0">Get alerted when a tracker drives too fast. Arrive/leave alerts live on a Place.</p>
                </div>
                <div class="d-flex ga-2">
                    <v-btn v-if="!authStore.isMinor" color="primary" prepend-icon="mdi-speedometer"
                           @click="openAdd('Speed')">
                        Add rule
                    </v-btn>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card variant="outlined" rounded="lg">
                    <v-table hover>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Assets</th>
                            <th class="text-center" style="width: 140px">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <template v-if="loading">
                            <tr>
                                <td colspan="4" class="text-center pa-8">
                                    <v-progress-circular color="primary" indeterminate size="32"/>
                                    <p class="text-body-2 text-medium-emphasis mt-3 mb-0">Loading alerts...</p>
                                </td>
                            </tr>
                        </template>
                        <template v-else-if="configTemplates.length">
                            <tr v-for="configTemplate in configTemplates" :key="configTemplate.id"
                                class="text-left">
                                <td class="font-weight-medium">{{ configTemplate.alertable?.label }}</td>
                                <td>
                                    <v-chip size="small" variant="tonal" color="orange">Speed</v-chip>
                                </td>
                                <td>
                                    <v-chip-group v-if="configTemplate.assets?.length">
                                        <v-chip v-for="asset in configTemplate.assets" :key="asset.id"
                                                size="small" variant="tonal">{{ asset.label }}</v-chip>
                                    </v-chip-group>
                                    <span v-else class="text-medium-emphasis text-body-2">No trackers assigned</span>
                                </td>
                                <td class="text-center">
                                    <v-btn v-if="!authStore.isMinor" icon="mdi-pencil-outline" size="small" variant="text" color="primary"
                                           :disabled="dialogSwitch" title="Edit"
                                           @click="editConfig(configTemplate)"/>
                                    <v-btn v-if="!authStore.isMinor" icon="mdi-delete-outline" size="small" variant="text" color="error"
                                           title="Delete"
                                           @click="deleteConfigTemplate(configTemplate)"/>
                                    <span v-if="authStore.isMinor" class="text-medium-emphasis text-caption">View only</span>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr>
                                <td colspan="4" class="text-center pa-10">
                                    <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-speedometer</v-icon>
                                    <p class="text-body-1 text-medium-emphasis mb-1">No speeding rules yet</p>
                                    <p class="text-body-2 text-medium-emphasis mb-3">
                                        Get notified when a tracker drives too fast.
                                    </p>
                                    <div class="d-flex ga-2 justify-center">
                                        <v-btn v-if="!authStore.isMinor" color="primary" prepend-icon="mdi-speedometer" size="small"
                                               @click="openAdd('Speed')">Add rule</v-btn>
                                    </div>
                                </td>
                            </tr>
                        </template>
                        </tbody>
                    </v-table>
                </v-card>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchConfigTemplates"
                ></v-pagination>
            </v-col>
        </v-row>
    </v-container>

    <Config v-if="dialogSwitch"
            v-model="dialogSwitch"
            :template="selectedConfigTemplate"
            :preset-type="presetType"
            :existing-names="existingNames"
            @addConfigTemplate="addConfigTemplate"
            @updateConfigTemplate="updateConfigTemplate"/>
</template>

<script setup>
import {ref, onMounted, computed} from "vue";
import {useRoute, useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import Config from "@/components/dialogs/monitoring/Config.vue";
import {useAuthStore} from "@/stores/authStore";

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const configTemplates = ref([]);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const dialogSwitch = ref(false);
const selectedConfigTemplate = ref(null);
const presetType = ref(null);
// Existing rule names for duplicate prevention in the Config dialog.
const existingNames = computed(() => configTemplates.value.map((c) => c.alertable?.label).filter(Boolean));
const {showSnackbar} = useSnackbarStore();

// Add a new speed rule with the type pre-chosen (skips the type picker).
const openAdd = (type) => {
    selectedConfigTemplate.value = null;
    presetType.value = type;
    dialogSwitch.value = true;
};

// A "Save Place" deep-link (?lat&lng[&name]) used to open a geofence alert here;
// geofence/place creation now lives on the Places page, so forward the preset
// there instead of opening the (speed-only) rules dialog.
const handlePresetFromQuery = () => {
    const {lat, lng, name} = route.query;
    const latN = Number(lat);
    const lngN = Number(lng);
    const valid = Number.isFinite(latN) && Number.isFinite(lngN)
        && latN >= -90 && latN <= 90 && lngN >= -180 && lngN <= 180;
    if (!valid) return;
    router.replace({name: 'places', query: {lat: latN, lng: lngN, name: name ?? undefined}});
};

onMounted(() => {
    // Open the preset dialog only after the list loads so the duplicate-name
    // check has the existing names.
    fetchConfigTemplates().finally(handlePresetFromQuery);
});
const fetchConfigTemplates = async () => {
    try {
        // Page through every config, then keep speed only. The list is filtered
        // client-side, so we cannot rely on server pagination (a speed rule could
        // sit on config page 2 behind geofence configs) - gather all and show
        // them on one page.
        const all = [];
        let page = 1;
        let last = 1;
        do {
            const resp = await send('get', 'monitoring/configs', {page});
            const pg = resp?.data?.data;
            all.push(...(pg?.data ?? []));
            last = Number(pg?.last_page) || page;
            page++;
        } while (page <= last && page <= 20);
        loading.value = false;
        // Speed-only surface: geofence/place alerts are managed from a Place.
        configTemplates.value = all.filter(
            (c) => c.monitoring_alertable_type === 'App\\Models\\MonitoringSpeedTemplate',
        );
        totalPages.value = 1;
    } catch (error) {
        console.log(error);
        loading.value = false;
        showSnackbar('Speeding rules could not be loaded', 'error');
    }
}
const addConfigTemplate = (configTemplate) => {
    configTemplates.value.unshift(configTemplate);
    dialogSwitch.value = false;
    selectedConfigTemplate.value = null;
}
const editConfig = (configTemplate) => {
    selectedConfigTemplate.value = configTemplate;
    presetType.value = null;
    dialogSwitch.value = true;
}
const updateConfigTemplate = (configTemplate) => {
    configTemplates.value.map((conf, index) => {
        if (conf.id === configTemplate.id) {
            configTemplates.value[index] = configTemplate;
        }
    });
    dialogSwitch.value = false;
    selectedConfigTemplate.value = null;
}
const deleteConfigTemplate = (configTemplate) => {
    if (confirm(`Are you sure to delete selected config template?`)) {
        send('delete', `monitoring/configs/${configTemplate.id}`)
            .then((response) => {
                if (response.data.success) {
                    configTemplates.value.map((item, index) => {
                        if (item.id === configTemplate.id) {
                            configTemplates.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`Config template could not be deleted`, 'error')
                }
            })
    }
}
</script>
